import { CalendarManager, CompanyEventDetails, CompanyEventsQueryParams, ISetParams } from '@benzinga/calendar-manager';
import { Session } from '@benzinga/session';

import { COMPANY_EVENTS_PAGE_SIZE, defaultCompanyEventsDateRange } from './constants';
import { logCalendarFetchFailure } from '../lib/utils/logCalendarFetchFailure';

function resolveCompanyEventsDateRange(
  params: CompanyEventsQueryParams,
  hasTickerFilter: boolean,
): { date_from?: string; date_to?: string } {
  const dateFrom = typeof params.date_from === 'string' && params.date_from !== '' ? params.date_from : undefined;
  const dateTo = typeof params.date_to === 'string' && params.date_to !== '' ? params.date_to : undefined;

  if (hasTickerFilter) {
    return { date_from: dateFrom, date_to: dateTo };
  }

  const defaults = defaultCompanyEventsDateRange();
  return {
    date_from: dateFrom ?? defaults.date_from ?? '',
    date_to: dateTo ?? defaults.date_to ?? '',
  };
}

function isCompanyEventsQueryParams(params: CompanyEventsQueryParams | ISetParams): params is CompanyEventsQueryParams {
  return 'date_from' in params || 'tickers' in params;
}

function toCompanyEventsQuery(
  params: CompanyEventsQueryParams | ISetParams,
  pageSize: number,
): CompanyEventsQueryParams {
  const pageSizeResolved = params.pageSize ?? pageSize;

  if (isCompanyEventsQueryParams(params)) {
    const { date_from, date_to } = resolveCompanyEventsDateRange(params, Boolean(params.tickers));
    return {
      date_from,
      date_to,
      importance: params.importance != null ? Number(params.importance) : undefined,
      pageSize: pageSizeResolved,
      tickers: params.tickers,
    };
  }

  const dateFrom = typeof params.dateFrom === 'string' ? params.dateFrom : undefined;
  const dateTo = typeof params.dateTo === 'string' ? params.dateTo : undefined;
  const symbols = params.symbols;
  const tickers = typeof symbols === 'string' ? symbols : Array.isArray(symbols) ? symbols.join(',') : undefined;
  const { date_from, date_to } = resolveCompanyEventsDateRange(
    {
      date_from: dateFrom,
      date_to: dateTo,
    },
    Boolean(tickers),
  );

  return {
    date_from,
    date_to,
    importance: params.importance != null ? Number(params.importance) : undefined,
    pageSize: pageSizeResolved,
    tickers,
  };
}

export const fetchCompanyEvents = (
  session: Session,
  params: CompanyEventsQueryParams | ISetParams = {},
  pageSize: number = COMPANY_EVENTS_PAGE_SIZE,
): Promise<CompanyEventDetails[]> => {
  return session
    .getManager(CalendarManager)
    .getCompanyEvents(toCompanyEventsQuery(params, pageSize))
    .then(res => {
      if (res?.err) {
        logCalendarFetchFailure('events', res.err);
        return [];
      }
      return res?.ok ?? [];
    })
    .catch(error => {
      logCalendarFetchFailure('events', error);
      return [];
    });
};
